import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';

interface CreateRateCardHooksOptions {
  resource: string;
  entityName: string;
}

export function createRateCardHooks<TItem, TInput>({
  resource,
  entityName,
}: CreateRateCardHooksOptions) {
  const queryKeys = {
    all: [resource] as const,
  };

  function useRates() {
    return useQuery({
      queryKey: queryKeys.all,
      queryFn: async () => {
        const response = await apiClient.get<{ items: TItem[] }>(`/settings/${resource}`);
        return response.items;
      },
    });
  }

  function useAddRate() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
      mutationFn: async (data: TInput) => {
        const response = await apiClient.post<{ item: TItem }>(`/settings/${resource}`, data);
        return response.item;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.all });
        toast({ title: 'Rate added' });
      },
      onError: (error: Error) => {
        toast({
          title: 'Error',
          description: error.message || `Failed to add ${entityName}.`,
          variant: 'destructive',
        });
      },
    });
  }

  function useUpdateRate() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
      mutationFn: async ({ id, ...data }: { id: string } & Partial<TInput>) => {
        const response = await apiClient.put<{ item: TItem }>(`/settings/${resource}/${id}`, data);
        return response.item;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.all });
        toast({ title: 'Rate updated' });
      },
      onError: (error: Error) => {
        toast({
          title: 'Error',
          description: error.message || `Failed to update ${entityName}.`,
          variant: 'destructive',
        });
      },
    });
  }

  function useDeleteRate() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`/settings/${resource}/${id}`);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.all });
        toast({ title: 'Rate deleted' });
      },
      onError: (error: Error) => {
        toast({
          title: 'Error',
          description: error.message || `Failed to delete ${entityName}.`,
          variant: 'destructive',
        });
      },
    });
  }

  return { queryKeys, useRates, useAddRate, useUpdateRate, useDeleteRate };
}
